/**
 * WaitMethodology — a collapsible, two-register explainer for how the estimated
 * wait is computed (issue #48). It sits inside the EstimatedWaitPanel, below the
 * per-triage figures, as a `<details>` (same disclosure pattern as TriageLegend)
 * so the panel stays uncluttered until tapped.
 *
 * Two registers, deliberately:
 *   1. Layman ("In parole semplici") — what the number actually means and how
 *      much to trust it, in plain language for a walk-in.
 *   2. Technical ("In dettaglio") — the smoothed drain-rate method + the caveats,
 *      faithful to Domain/Services/WaitTimeEstimator.cs.
 *
 * Kept honest per DECISIONS.md: QUEUE_LENGTH is people currently waiting (a
 * gauge), not cumulative arrivals; the rate is reconstructed from deltas → an
 * estimate, never a promise.
 */

export function WaitMethodology() {
  return (
    <details className="wait-method">
      <summary className="wait-method__summary">Come calcoliamo l'attesa stimata?</summary>

      <div className="wait-method__block">
        <h3 className="wait-method__heading">In parole semplici</h3>
        <p className="wait-method__text">
          Non è un tempo garantito, ma una stima di massima. Guardiamo quante persone sono in coda
          davanti a te — cioè quelle con un codice di pari o maggiore urgenza, che vengono viste
          prima — e quanto in fretta il pronto soccorso ha smaltito la coda nelle ultime ore.
          Dividendo l'una per l'altra otteniamo un'attesa indicativa.
        </p>
        <p className="wait-method__text">
          Quanto fidarti? Te lo diciamo con l'etichetta di affidabilità accanto a ogni stima: più a
          lungo abbiamo potuto osservare la coda e più è breve l'attesa prevista, più la stima è
          solida. Le attese molto lunghe sono le più incerte. E quando la coda non si sta smaltendo
          non inventiamo un numero: scriviamo «non stimabile» invece di un falso «0».
        </p>
      </div>

      <div className="wait-method__block">
        <h3 className="wait-method__heading">In dettaglio</h3>
        <p className="wait-method__text">
          Il dato pubblico dice solo quante persone sono in attesa in ogni istante (un livello, non
          gli arrivi). Da lì ricostruiamo un <strong>tasso di&nbsp;smaltimento</strong>:
        </p>
        <ol className="wait-method__steps">
          <li>
            prendiamo la coda totale nelle ultime ~2 ore e la mediamo in intervalli da 15 minuti,
            per smorzare il rumore delle singole rilevazioni (una ogni ~5 min);
          </li>
          <li>
            sommiamo i cali sostenuti da un intervallo al successivo: è il ritmo con cui la coda si
            svuota, in&nbsp;persone/ora;
          </li>
          <li>
            per un nuovo arrivo con un dato codice contiamo le <em>persone davanti</em> (tutti
            quelli in attesa con codice di pari o maggiore urgenza) e calcoliamo: attesa ≈ persone
            davanti ÷ ritmo di smaltimento.
          </li>
        </ol>
        <p className="wait-method__text">
          Perché è una stima e non una promessa: ignora i nuovi arrivi (che allungano l'attesa),
          assume un'unica «corsia» di servizio condivisa, e un ritmo ricostruito dai conteggi non
          separa alla perfezione chi entra da chi esce. Per questo abbassiamo l'affidabilità quando
          l'attesa prevista è molto lunga (oltre ~3 ore) e non mostriamo alcun numero quando la coda
          non sta calando.
        </p>
      </div>

      <p className="wait-method__source">
        Fonte: conteggi in tempo reale del feed CC0 PS_Queue (Open Data Südtirol).
      </p>
    </details>
  );
}
